import { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Logo } from "../Brand";

const NAV = [
  ["/", "Home"], ["/features", "Features"], ["/ims", "IMS / QMS"], ["/business-tools", "Business Tools"],
  ["/consultants", "For Consultants"], ["/pricing", "Pricing"], ["/about", "About"], ["/contact", "Contact"],
];

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

function Nav() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => {
    setOpen(false);
    window.scrollTo(0, 0);
  }, [loc.pathname]);
  const link = ({ isActive }) => `text-sm font-medium transition-colors ${isActive ? "text-sky-700" : "text-slate-600 hover:text-slate-900"}`;
  return (
    <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-slate-200/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-6">
        <Link to="/" data-testid="nav-home-logo"><Logo /></Link>
        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map(([to, label]) => (
            <NavLink key={to} to={to} end className={link} data-testid={`nav-${slug(label)}`}>{label}</NavLink>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <Link to="/login" className="text-sm font-semibold text-slate-700 hover:text-slate-900 px-3 py-2" data-testid="nav-login-btn">Log In</Link>
          <Link to="/register" className="text-sm font-semibold bg-sky-600 hover:bg-sky-700 text-white px-4 py-2.5 rounded-md transition-colors" data-testid="nav-start-trial-btn">Start Free Trial</Link>
        </div>
        <button className="lg:hidden p-2" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="nav-mobile-toggle">{open ? <X /> : <Menu />}</button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white px-4 py-4 space-y-1" data-testid="nav-mobile-menu">
          {NAV.map(([to, label]) => (
            <NavLink key={to} to={to} end className="block py-2 text-slate-700 font-medium">{label}</NavLink>
          ))}
          <div className="flex gap-3 pt-3">
            <Link to="/login" className="flex-1 text-center border border-slate-300 rounded-md py-2.5 font-semibold">Log In</Link>
            <Link to="/register" className="flex-1 text-center bg-sky-600 text-white rounded-md py-2.5 font-semibold">Start Free Trial</Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  const col = (title, links) => (
    <div>
      <h4 className="text-white font-semibold text-sm mb-4">{title}</h4>
      <ul className="space-y-2.5">
        {links.map(([to, l]) => (
          <li key={to}><Link to={to} className="text-sm text-slate-400 hover:text-white transition-colors">{l}</Link></li>
        ))}
      </ul>
    </div>
  );
  return (
    <footer className="bg-[#0A192F] text-slate-300" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-2 md:grid-cols-5 gap-10">
        <div className="col-span-2">
          <Logo light />
          <p className="mt-4 text-sm text-slate-400 max-w-xs leading-relaxed">Integrated Quality, Safety &amp; Compliance Management Software for Australian businesses.</p>
          <p className="mt-4 text-sm text-slate-500">Quality. Safety. Compliance. All in One Place.</p>
        </div>
        {col("Product", [["/features", "Features"], ["/ims", "IMS / QMS"], ["/business-tools", "Business Tools"], ["/pricing", "Pricing"]])}
        {col("Company", [["/about", "About"], ["/contact", "Contact & Support"], ["/login", "Customer Login"], ["/register", "Start Free Trial"]])}
        {col("Legal", [["/legal/privacy", "Privacy Policy"], ["/legal/terms", "Terms of Service"], ["/legal/cancellation", "Cancellation Policy"], ["/legal/data-retention", "Data Retention"]])}
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row justify-between gap-2 text-xs text-slate-500">
          <span>© {new Date().getFullYear()} QORDEL 360. All prices in AUD. [ABN placeholder]</span>
          <span>Using QORDEL 360 does not by itself guarantee ISO certification or legal compliance.</span>
        </div>
      </div>
    </footer>
  );
}

export default function SiteLayout() {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Nav />
      <main className="flex-1"><Outlet /></main>
      <Footer />
    </div>
  );
}
